import { Layout, Space } from 'antd';
import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  Outlet, useLocation, useNavigate,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useMediaQuery } from 'react-responsive';
import { MAIN_COLOR, WHITE_BG } from '../../styles/color';
import useScrollToTop from '../../hooks/useScrollToTop';
import { selectBookingAppointmentData, selectBookingClientData } from '../../redux/slices/booking';
import { AppointmentData } from '../../model';
import {
  DOCTORS_ROUTE, PHN_DOCTORS_ROUTE, PHN_ROUTE, STANDALONE_PATHS,
} from '../../constants/routes';
import { getPrivateHeaderConfig } from '../../config/privateBooking';
import FeeChangeBanner, { FEE_CHANGE_BANNER_HEIGHT } from '../../components/FeeChangeBanner';
import { useFeeChangeNoticeOnPsychologistList } from '../../hooks/useFeeChangeNoticeOnPsychologistList';
import { useMeasuredHeight } from '../../hooks/useMeasuredHeight';

const { Header, Content } = Layout;

const DEFAULT_HEADER_HEIGHT = 64;

const LOGO_HEIGHT = { mobile: 32, desktop: 44 };
const HEADER_PADDING = { mobile: '8px 12px', desktop: '10px 50px' };

const BookingHome: FC = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const appointment = useSelector(selectBookingAppointmentData) as AppointmentData;
  const client = useSelector(selectBookingClientData);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(search);
  const code = searchParams.get('code');
  const isPublicBookingEnabled = process.env.REACT_APP_ENABLE_PUBLIC_BOOKING === 'true';
  const isPrivateBookingEnabled = process.env.REACT_APP_ENABLE_PRIVATE_BOOKING === 'true';
  const defaultHeaderConfig = {
    logoHref: 'https://virtualpsychology.amplarhealth.com.au/',
    logoSrc: '/amplar-logo.svg',
    contactHref: 'https://virtualpsychology.amplarhealth.com.au/contact',
  };

  const headerConfig = getPrivateHeaderConfig() ?? defaultHeaderConfig;

  const showFeeBanner = useFeeChangeNoticeOnPsychologistList();
  const [bannerHeight, setBannerHeight] = useState(FEE_CHANGE_BANNER_HEIGHT);
  const handleBannerHeightChange = useCallback((height: number) => setBannerHeight(Math.ceil(height)), []);
  const bannerOffset = showFeeBanner ? bannerHeight : 0;
  const headerMarginTop = isMobile || showFeeBanner ? 0 : 12;
  const headerMarginBottom = isMobile ? 0 : 12;
  const headerMargin = `${headerMarginTop}px 0 ${headerMarginBottom}px`;
  const { height: headerHeight } = useMeasuredHeight<HTMLElement>(DEFAULT_HEADER_HEIGHT, '.ant-layout-header');

  useEffect(() => {
    if (pathname.startsWith(`${PHN_ROUTE}`) && code === 'X7mP92qL' && isPrivateBookingEnabled) {
      if (isEmpty(appointment.specialistId) && !STANDALONE_PATHS.includes(pathname)) {
        navigate(`${PHN_DOCTORS_ROUTE}${search}`, { replace: true });
      }
    } else if ((isEmpty(appointment) || isEmpty(appointment.specialistId)) && !STANDALONE_PATHS.includes(pathname) && isPublicBookingEnabled) {
      navigate(`${DOCTORS_ROUTE}${search}`, { replace: true });
    }
  }, [appointment, client, navigate, pathname, search, code, isPublicBookingEnabled, isPrivateBookingEnabled]);

  useScrollToTop();

  return (
    <Layout style={{
      position: 'absolute',
      minHeight: '100vh',
      width: '100%',
      backgroundColor: WHITE_BG,
      padding: isMobile ? '4px' : '0',
    }}
    >
      {showFeeBanner && <FeeChangeBanner onHeightChange={handleBannerHeightChange} />}
      <Header style={{
        position: 'fixed',
        top: bannerOffset,
        zIndex: 1,
        width: '100%',
        height: 'auto',
        lineHeight: 'normal',
        backgroundColor: WHITE_BG,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: headerMargin,
        padding: isMobile ? HEADER_PADDING.mobile : HEADER_PADDING.desktop,
      }}
      >
        <div style={{
          width: isMobile ? '95%' : '80%', maxWidth: '1440px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
        >
          <Space size={10}>
            <a href={headerConfig.logoHref} target="_blank" rel="noreferrer">
              <img
                src={headerConfig.logoSrc}
                alt="Booking logo"
                height={isMobile ? LOGO_HEIGHT.mobile : LOGO_HEIGHT.desktop}
                style={{ display: 'block' }}
              />
            </a>
          </Space>
          <a href={headerConfig.contactHref} target="_blank" rel="noreferrer" style={{ color: MAIN_COLOR, fontSize: '16px', fontWeight: 500 }}>
            Contact Us
          </a>
        </div>
      </Header>
      <Content style={{ marginTop: bannerOffset + headerMarginTop + headerHeight + headerMarginBottom, marginBottom: 62 }}>
        <Outlet />
      </Content>
    </Layout>
  );
};

export default BookingHome;
